import type { Incident, IncidentGroup } from '../../services/incidentsApi';
import { incidentTypeLabelForRow } from './incidentTypeLabels';
import { presentAreaLabel } from '../../utils/presentAreaLabel';
import {
  incidentCanonicalEvidenceReadinessLabel,
  incidentCanonicalFollowupOwnerLabel,
} from '../../components/incidents/reviewCanonical';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import { buyerConditionDisplayName, looksLikeInternalSafetyId } from '../../domain/buyerFacing/conditionDisplayNames';

type IncidentLike = Incident | IncidentGroup | Record<string, unknown> | null | undefined;

export type IncidentPolicyContextItem = {
  label: string;
  value: string;
};

export type IncidentDisplayChip = {
  label: string;
  tone?: 'default' | 'info' | 'success' | 'warning' | 'error';
};

const safeText = (value: unknown): string => String(value || '').trim();

const titleCase = (value: string): string =>
  String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}` : word))
    .join(' ');

export const formatCompactDuration = (seconds?: number | null): string => {
  if (seconds == null || !Number.isFinite(seconds)) return '';
  const total = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(total / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  const remMins = mins % 60;
  const remSecs = total % 60;
  if (days > 0) return `${days}d ${remHours}h`;
  if (hours > 0) return `${hours}h ${remMins}m`;
  if (mins > 0) return `${mins}m ${remSecs}s`;
  return `${remSecs}s`;
};

const zoneBehaviorPhrase = (zoneBehavior: string): string => {
  const normalized = safeText(zoneBehavior).toLowerCase();
  if (!normalized) return 'a monitored area';
  if (normalized === 'restricted') return 'a danger zone';
  if (normalized === 'validation') return 'the validation area';
  return `the ${normalized} area`;
};

const getMetrics = (row: IncidentLike): Record<string, any> => {
  const record = (row || {}) as any;
  return record?.metrics && typeof record.metrics === 'object' ? record.metrics : {};
};

const getAttributes = (row: IncidentLike): Record<string, any> => {
  const metrics = getMetrics(row);
  return metrics?.attributes && typeof metrics.attributes === 'object' ? metrics.attributes : {};
};

const getIncidentTypeKey = (row: IncidentLike, resolvePolicyType?: (row: IncidentLike) => string | null | undefined): string => {
  const record = (row || {}) as any;
  const incidentType = safeText(record?.incident_type).toLowerCase();
  if (incidentType && incidentType !== 'unknown' && incidentType !== 'other') return incidentType;

  const policyType = safeText(record?.policy_type || record?.triggered_by?.policy_type).toLowerCase();
  if (policyType) return policyType;

  const resolved = safeText(resolvePolicyType?.(record)).toLowerCase();
  if (resolved) return resolved;

  const taxonomyType = safeText(record?.taxonomy?.incident_type || record?.taxonomy?.category).toLowerCase();
  if (taxonomyType) return taxonomyType;

  return '';
};

const getDurationSeconds = (row: IncidentLike): number | null => {
  const record = (row || {}) as any;
  const metrics = getMetrics(record);
  const attrs = getAttributes(record);
  const rawValue =
    record?.duration_seconds ??
    metrics?.duration_seconds ??
    metrics?.active_duration_seconds ??
    attrs?.duration_seconds;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getSubjectLabel = (row: IncidentLike): string => {
  const metrics = getMetrics(row);
  const attrs = getAttributes(row);
  const subject = safeText(attrs?.subject || metrics?.subject || attrs?.actor_type || metrics?.actor_type);
  if (!subject) return 'Entity';
  if (subject.toLowerCase() === 'person') return 'Person';
  if (subject.toLowerCase() === 'pedestrian') return 'Pedestrian';
  if (subject.toLowerCase() === 'forklift') return 'Forklift';
  return titleCase(subject);
};

const getZoneBehavior = (row: IncidentLike): string => {
  const record = (row || {}) as any;
  const metrics = getMetrics(record);
  const attrs = getAttributes(record);
  const explicit = safeText(
    attrs?.zone_behavior ||
      metrics?.zone_behavior ||
      attrs?.region_behavior ||
      metrics?.region_behavior ||
      record?.raw?.zone_behavior,
  );
  if (explicit) return titleCase(explicit);

  const typeKey = getIncidentTypeKey(record);
  if (['zone_violation', 'zone_intrusion', 'intrusion', 'dwell_violation', 'loitering'].includes(typeKey)) {
    return 'Restricted';
  }
  if (['ppe_violation', 'ppe_compliance'].includes(typeKey)) {
    return 'Validation';
  }
  return '';
};

const getSensitivity = (row: IncidentLike): string => {
  const record = (row || {}) as any;
  const metrics = getMetrics(record);
  const attrs = getAttributes(record);
  const explicit = safeText(attrs?.sensitivity || metrics?.sensitivity || record?.raw?.sensitivity);
  if (explicit) return titleCase(explicit);

  const severity = safeText(record?.severity).toLowerCase();
  if (severity === 'critical') return 'High';
  if (severity === 'high') return 'Elevated';
  if (severity === 'medium') return 'Balanced';
  if (severity === 'low') return 'Relaxed';
  return '';
};

const getScopeSummary = (row: IncidentLike, options?: { zoneName?: string | null; cameraName?: string | null }): string => {
  const record = (row || {}) as any;
  const zone = presentAreaLabel(safeText(options?.zoneName || record?.zone_id));
  const camera = safeText(options?.cameraName || record?.camera_id);
  const device = safeText(record?.device_id);
  const parts = [
    zone,
    camera ? `Camera ${camera}` : '',
    device ? `Device ${device}` : '',
  ].filter(Boolean);
  return parts.join(' · ');
};

export function incidentDisplayTitleForRow(
  row: IncidentLike,
  resolvePolicyType?: (row: IncidentLike) => string | null | undefined,
  _options?: { zoneName?: string | null },
): string {
  return incidentTypeLabelForRow(row as any, resolvePolicyType as any);
}

/** Operator drawer title: Safety condition name, never a policy/detector ID. */
export function operatorObservationDrawerTitle(args: {
  policyLabel?: string | null;
  row?: IncidentLike;
  fallback?: string;
}): string {
  const record = (args.row || {}) as any;
  const incidentType = safeText(record?.incident_type || record?.risk_type);
  const policyLabel = safeText(args.policyLabel);
  const preferred = looksLikeInternalSafetyId(policyLabel) ? incidentType : (policyLabel || incidentType);
  const catalogued = buyerConditionDisplayName(preferred, incidentType);
  if (catalogued && catalogued !== 'Safety condition' && !looksLikeInternalSafetyId(catalogued)) {
    return catalogued;
  }
  const fromRow = incidentDisplayTitleForRow(record);
  if (fromRow && !looksLikeInternalSafetyId(fromRow)) return fromRow;
  return args.fallback || 'Observation';
}

export function incidentDisplaySummaryForRow(
  row: IncidentLike,
  options?: {
    zoneName?: string | null;
    cameraName?: string | null;
    deviceName?: string | null;
    resolvePolicyType?: (row: IncidentLike) => string | null | undefined;
    includeLocation?: boolean;
  },
): string {
  const record = (row || {}) as any;
  const typeKey = getIncidentTypeKey(record, options?.resolvePolicyType);
  const duration = getDurationSeconds(record);
  const zoneBehavior = getZoneBehavior(record);
  const subject = getSubjectLabel(record);
  const scopeLabel = options?.includeLocation === false ? '' : presentAreaLabel(safeText(options?.zoneName || record?.zone_id));
  const scopePhrase = scopeLabel ? ` in ${scopeLabel}` : '';
  const durationPhrase = duration ? ` for ${formatCompactDuration(duration)}` : '';

  switch (typeKey) {
    case 'zone_violation':
    case 'zone_intrusion':
    case 'intrusion':
    case 'region_entrance':
      if (subject === 'Pedestrian' && zoneBehaviorPhrase(zoneBehavior) === 'a danger zone') {
        return `Pedestrian in danger zone${scopeLabel ? ` in ${scopeLabel}` : ''}.`;
      }
      return `${subject} entered ${zoneBehaviorPhrase(zoneBehavior)}${scopePhrase}.`;
    case 'dwell_violation':
    case 'loitering':
      return `${subject} remained in ${zoneBehaviorPhrase(zoneBehavior)}${durationPhrase || ' longer than expected'}${scopePhrase}.`;
    case 'ppe_violation':
    case 'ppe_compliance':
      return `Person detected without required PPE${scopePhrase || (zoneBehavior ? ` in ${zoneBehaviorPhrase(zoneBehavior)}` : '')}.`;
    case 'forklift_proximity':
    case 'pedestrian_near_moving_forklift':
    case 'pedestrian_forklift':
      return `Person and forklift came into unsafe proximity${scopePhrase}.`;
    case 'occupancy':
      return `Area occupancy exceeded the configured limit${scopePhrase}.`;
    case 'line_crossing': {
      const metrics = getMetrics(record);
      const attrs = getAttributes(record);
      const lineName = safeText(metrics?.line_name || attrs?.line_name);
      return lineName
        ? `Entity crossed monitored boundary ${lineName}${scopePhrase}.`
        : `Entity crossed a monitored boundary${scopePhrase}.`;
    }
    case 'worker_down':
      return `Potential worker-down event detected${scopePhrase}.`;
    case 'forklift_speeding':
      return `Forklift speed exceeded the configured limit${scopePhrase}.`;
    default: {
      const description = safeText(record?.description || record?.summary_text);
      if (description && !/\bpolicy\b/i.test(description)) {
        const identifiers = [
          [safeText(record?.zone_id), safeText(options?.zoneName)],
          [safeText(record?.camera_id), safeText(options?.cameraName)],
          [safeText(record?.device_id), safeText(options?.deviceName)],
        ];
        return identifiers.reduce((display, [id, label]) => {
          if (!id || !label || id === label) return display;
          return display.replace(new RegExp(`\\b${id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), label);
        }, description);
      }
      return `${incidentDisplayTitleForRow(record, options?.resolvePolicyType)} requires operator review${scopePhrase}.`;
    }
  }
}

export function incidentPolicyContextForRow(
  row: IncidentLike,
  options?: { zoneName?: string | null; cameraName?: string | null },
): IncidentPolicyContextItem[] {
  const record = (row || {}) as any;
  const items: IncidentPolicyContextItem[] = [];
  const policyLabel = safeText(
    record?.policy_name || record?.triggered_by?.policy_name || record?.policy_id || record?.triggered_by?.policy_id,
  );
  if (policyLabel) items.push({ label: 'Detection rule', value: policyLabel });

  const policyVersionRaw = record?.policy_version ?? record?.safety_case?.traceability?.policy_version;
  const policyVersion = Number(policyVersionRaw);
  if (Number.isFinite(policyVersion) && policyVersion > 0) {
    items.push({ label: 'Policy version', value: `v${policyVersion}` });
  }

  const zoneBehavior = getZoneBehavior(record);
  if (zoneBehavior) items.push({ label: 'Area behavior', value: zoneBehavior });

  const sensitivity = getSensitivity(record);
  if (sensitivity) items.push({ label: 'Sensitivity', value: sensitivity });

  const scope = getScopeSummary(record, options);
  if (scope) items.push({ label: 'Scope', value: scope });

  return items;
}

export function incidentInsightChipsForRow(
  row: IncidentLike,
  options?: {
    currentUser?: string | null;
    includeOwnership?: boolean;
    includeEvidence?: boolean;
    includeDuration?: boolean;
    includeRelatedAlerts?: boolean;
    includeGrouped?: boolean;
  },
): IncidentDisplayChip[] {
  const record = (row || {}) as any;
  const chips: IncidentDisplayChip[] = [];
  const relatedAlerts = Math.max(0, Number(record?.alert_count || 0));
  const recurrenceTag = safeText(record?.recurrence_tag);
  const recurrenceDisplay = (() => {
    if (!recurrenceTag) return '';
    const lower = recurrenceTag.toLowerCase();
    if (lower === 'recurring hotspot' || lower === 'recurring hotspots') {
      return lower === 'recurring hotspots' ? 'Recurring risk patterns' : 'Recurring risk pattern';
    }
    return recurrenceTag;
  })();
  const duration = getDurationSeconds(record);
  const assignee = incidentCanonicalFollowupOwnerLabel(record) || safeText(record?.owner);
  const currentUser = safeText(options?.currentUser);
  const status = safeText(record?.status).toUpperCase();

  if (recurrenceDisplay) chips.push({ label: recurrenceDisplay, tone: 'warning' });

  if (options?.includeRelatedAlerts !== false && relatedAlerts > 1) {
    chips.push({ label: `${relatedAlerts} pattern observations`, tone: 'info' });
  }
  if (options?.includeDuration && duration) {
    chips.push({ label: `Active for ${formatCompactDuration(duration)}`, tone: 'default' });
  }
  if (safeText(record?.confidence_indicator).toLowerCase() === 'high') chips.push({ label: 'High confidence', tone: 'success' });
  if (record?.ops_overdue) chips.push({ label: 'Overdue', tone: 'error' });
  if (options?.includeGrouped !== false && record?.is_grouped) chips.push({ label: 'Recurring risk pattern', tone: 'default' });

  if (options?.includeOwnership !== false && status !== 'CLOSED' && status !== 'SUPPRESSED') {
    if (currentUser && assignee && assignee.toLowerCase() === currentUser.toLowerCase()) chips.push({ label: 'Mine', tone: 'info' });
    else if (assignee) chips.push({ label: `${displaySafetyManagerLabel('followup_owner')}: ${assignee}`, tone: 'default' });
    else chips.push({ label: `${displaySafetyManagerLabel('followup_owner')} unassigned`, tone: 'default' });
  }

  if (options?.includeEvidence) {
    const attachments = Array.isArray(record?.evidence?.attachments) ? record.evidence.attachments.length : 0;
    const snapshotStatus = safeText(record?.snapshot_status).toUpperCase();
    const clipStatus = safeText(record?.clip_status).toUpperCase();
    const evidenceCount = Math.max(0, Number(record?.evidence_count || 0));
    const readinessLabel = incidentCanonicalEvidenceReadinessLabel(record);

    if (readinessLabel) {
      const lower = readinessLabel.toLowerCase();
      chips.push({
        label: `Evidence: ${readinessLabel}`,
        tone:
          lower === 'ready'
            ? 'success'
            : lower === 'failed'
              ? 'error'
              : lower === 'pending' || lower === 'unavailable'
                ? 'default'
                : 'warning',
      });
    } else
    if (attachments > 0) chips.push({ label: 'Evidence file attached', tone: 'success' });
    else if (snapshotStatus === 'AVAILABLE' || clipStatus === 'AVAILABLE') chips.push({ label: 'Evidence package ready', tone: 'success' });
    else if (snapshotStatus === 'PENDING' || clipStatus === 'PENDING') chips.push({ label: 'Snapshot pending', tone: 'warning' });
    else if (evidenceCount > 0) chips.push({ label: 'Related evidence in group', tone: 'info' });
  }

  return chips.slice(0, 5);
}
